import React, { useEffect, useMemo, useRef, useState } from "react";
import { db, auth, storage } from "../firebase";
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, collection, query, where, getDocs } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { FaFileContract, FaPlus, FaTrash, FaExclamationTriangle, FaArrowUp, FaPen, FaFileUpload, FaExternalLinkAlt } from "react-icons/fa";
import AdminModal from "./AdminModal";
import FormField from "./FormField";
import StatusBadge from "./StatusBadge";
import DataTable from "./DataTable";
import { useToast } from "./Toast";
import { useAppDialog } from "./AppDialog";
import { isDemoUser, DEMO_BLOCK_MESSAGE } from "../utils/demoMode";
import { logAudit } from "../utils/auditLog";
import { formatCurrencyUY } from "../utils/formatters";
import { getAllActiveFunds } from "../utils/fundHelpers";
import { esPagoDeAlquiler } from "../utils/unidadesEdificio";
import {
  MESES,
  MES_COBRO_ADELANTADO,
  MES_COBRO_VENCIDO,
  periodoKey,
  nombrePeriodo,
  mesSiguiente,
  periodoDeAlquiler,
  tramoVigente,
  validarTramos,
  alertasDeAlquiler,
  ajustarMonto,
} from "../utils/alquileresEdificio";
import { colors, radius, spacing, typography, cardBase, btnPrimary, btnSecondary, btnGhost, inputBase } from "../utils/designTokens";

/*
 * Contrato de una unidad "Alquiler del edificio" (alquileres/{apartmentId}).
 * El cierre de cada mes le cobra al inquilino el monto del tramo vigente y lo
 * cobrado entra como ingreso al fondo elegido.
 *  - Cargar contrato: monto mensual y desde qué mes (un tramo sin fin).
 *  - Ajustar monto: cierra el tramo vigente y abre uno nuevo, sin dar de baja.
 *  - Editar: fondo, mes de cobro, estado, nota y corrección de tramos.
 *  - El contrato firmado se sube a Storage (documentos del edificio) y queda
 *    en el campo `contrato` ({ url, nombre, path, subidoEn, subidoPor }).
 * Las reglas permiten solo estos campos y no dejan borrar: la baja es active:false.
 */

const TIPOS_ARCHIVO = ".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx";
const MAX_MB = 25;

const hoy = () => new Date();
const periodoActual = () => ({ month: hoy().getMonth() + 1, year: hoy().getFullYear() });
const keyDe = (p) => periodoKey(p.month, p.year);
const proximoMesKey = () => keyDe(mesSiguiente(periodoActual()));

const formDeContrato = (c) => ({
  targetFundId: c?.targetFundId || "reserva",
  mesCobro: c?.mesCobro === MES_COBRO_VENCIDO ? MES_COBRO_VENCIDO : MES_COBRO_ADELANTADO,
  active: c ? c.active !== false : true,
  nota: c?.nota || "",
  tramos: (c?.tramos || []).map((t) => ({ desde: t.desde || "", hasta: t.hasta || "", monto: t.monto ?? "", nota: t.nota || "" })),
});

/** Tramos del formulario como se guardan: hasta null si no tiene fin, monto Number, ordenados. */
const tramosParaGuardar = (tramos) =>
  tramos
    .map((t) => ({ desde: t.desde, hasta: t.hasta ? t.hasta : null, monto: Number(t.monto), nota: (t.nota || "").trim() }))
    .sort((a, b) => a.desde.localeCompare(b.desde));

/**
 * Mes y año con dos desplegables (en vez del selector nativo de mes, que en
 * algunos navegadores muestra "---------- de ----"). value/onChange en "YYYY-MM".
 * Con `sinFin`, un tilde "Sin fecha de fin" deja el valor vacío.
 */
export function SelectorMes({ id, value, onChange, sinFin = false, anios }) {
  const [y, m] = value ? value.split("-").map(Number) : [null, null];
  const lista = anios || rangoAnios(value);
  const vacio = !value;
  const cambiar = (nuevoMes, nuevoAnio) => {
    if (!nuevoMes || !nuevoAnio) return;
    onChange(periodoKey(nuevoMes, nuevoAnio));
  };
  return (
    <div style={styles.selectorMes}>
      <select
        id={id}
        aria-label="Mes"
        className="habitia-input"
        style={styles.input}
        value={m || ""}
        disabled={sinFin && vacio}
        onChange={(e) => cambiar(Number(e.target.value), y || periodoActual().year)}
      >
        <option value="" disabled>Mes</option>
        {MESES.map((nombre, i) => <option key={nombre} value={i + 1}>{nombre}</option>)}
      </select>
      <select
        aria-label="Año"
        className="habitia-input"
        style={styles.input}
        value={y || ""}
        disabled={sinFin && vacio}
        onChange={(e) => cambiar(m || periodoActual().month, Number(e.target.value))}
      >
        <option value="" disabled>Año</option>
        {lista.map((a) => <option key={a} value={a}>{a}</option>)}
      </select>
      {sinFin && (
        <label style={styles.checkChico}>
          <input
            type="checkbox"
            checked={vacio}
            onChange={(e) => onChange(e.target.checked ? "" : proximoMesKey())}
          />
          Sin fecha de fin
        </label>
      )}
    </div>
  );
}

/** Años del desplegable: del año pasado a 5 adelante, más el del valor si queda afuera. */
function rangoAnios(value) {
  const actual = hoy().getFullYear();
  const anios = [];
  for (let a = actual - 1; a <= actual + 5; a += 1) anios.push(a);
  const y = value ? Number(value.split("-")[0]) : null;
  if (y && !anios.includes(y)) anios.push(y);
  return anios.sort((a, b) => a - b);
}

const ContratoAlquilerCard = ({ apartmentId, apartmentNumber, buildingId, building }) => {
  const { showToast } = useToast();
  const { confirmDialog } = useAppDialog();
  const [contrato, setContrato] = useState(null); // null = sin contrato
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState(false);
  const [hayAlquileresCobrados, setHayAlquileresCobrados] = useState(false);
  const [modal, setModal] = useState(null); // "nuevo" | "ajuste" | "editar" | null
  const [form, setForm] = useState(formDeContrato(null));
  const [alta, setAlta] = useState({ monto: "", desde: proximoMesKey() });
  const [ajuste, setAjuste] = useState({ monto: "", desde: proximoMesKey(), nota: "" });
  const [archivo, setArchivo] = useState(null);
  const [errores, setErrores] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const inputArchivo = useRef(null);

  const fondos = useMemo(() => getAllActiveFunds(building || {}), [building]);
  const nombreFondo = (id) => fondos.find((f) => f.id === id)?.name || id;

  useEffect(() => {
    if (!apartmentId) return;
    let vivo = true;
    (async () => {
      setCargando(true);
      setErrorCarga(false);
      try {
        const snap = await getDoc(doc(db, "alquileres", String(apartmentId)));
        if (vivo) setContrato(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      } catch (err) {
        console.error("[ContratoAlquiler] no se pudo leer el contrato:", err);
        if (vivo) setErrorCarga(true);
      }
      try {
        // Pagos de esta unidad (las reglas dejan al staff listarlos por apartmentId):
        // si ya se cobraron alquileres, cambiar el mes de cobro pide confirmación.
        const pagos = await getDocs(query(collection(db, "payments"), where("apartmentId", "==", String(apartmentId))));
        if (vivo) setHayAlquileresCobrados(pagos.docs.some((d) => esPagoDeAlquiler(d.data())));
      } catch (err) {
        console.warn("[ContratoAlquiler] no se pudieron leer los pagos de la unidad:", err?.code || err);
      }
      if (vivo) setCargando(false);
    })();
    return () => {
      vivo = false;
    };
  }, [apartmentId]);

  const alertas = useMemo(() => alertasDeAlquiler(contrato), [contrato]);
  // Lo que cobra el próximo cierre (el del mes en curso).
  const proximoCobro = useMemo(() => {
    if (!contrato) return null;
    const per = periodoDeAlquiler(contrato, periodoActual().month, periodoActual().year);
    return { periodo: per, tramo: tramoVigente(contrato, keyDe(per)) };
  }, [contrato]);

  const quien = () => ({ email: auth.currentUser?.email || "", uid: auth.currentUser?.uid || null });

  const abrirNuevo = () => {
    setForm(formDeContrato(null));
    setAlta({ monto: "", desde: proximoMesKey() });
    setArchivo(null);
    setErrores([]);
    setModal("nuevo");
  };
  const abrirAjuste = () => {
    setAjuste({ monto: "", desde: proximoMesKey(), nota: "" });
    setErrores([]);
    setModal("ajuste");
  };
  const abrirEditar = () => {
    setForm(formDeContrato(contrato));
    setArchivo(null);
    setErrores([]);
    setModal("editar");
  };
  const cerrar = () => { if (!guardando) setModal(null); };

  /** Sube el archivo del contrato a los documentos del edificio. */
  const subirArchivo = async (file) => {
    const safe = file.name.replace(/[^\w.-]+/g, "_").slice(-80);
    const path = `buildings/${buildingId}/documentos/${Date.now()}_contrato_alquiler_${apartmentId}_${safe}`;
    const fileRef = storageRef(storage, path);
    await uploadBytes(fileRef, file, { contentType: file.type || undefined });
    return { url: await getDownloadURL(fileRef), nombre: file.name, path, subidoEn: serverTimestamp(), subidoPor: quien().email };
  };

  const validarArchivo = (file) => {
    if (!file) return null;
    if (file.size > MAX_MB * 1024 * 1024) return `El archivo pesa más de ${MAX_MB} MB.`;
    return null;
  };

  /** Escribe el contrato (alta o cambios) y lo refleja en pantalla. */
  const escribir = async (cambios, accion) => {
    const ref = doc(db, "alquileres", String(apartmentId));
    const q = quien();
    if (!contrato) {
      const nuevo = {
        buildingid: Number(buildingId),
        apartmentId: String(apartmentId),
        apartmentNumber: apartmentNumber != null ? String(apartmentNumber) : null,
        ...cambios,
        createdAt: serverTimestamp(),
        createdBy: q.email,
        createdByUid: q.uid,
      };
      await setDoc(ref, nuevo);
      setContrato({ id: String(apartmentId), ...nuevo, createdAt: new Date() });
    } else {
      const upd = { ...cambios, updatedAt: serverTimestamp(), updatedBy: q.email, updatedByUid: q.uid };
      await updateDoc(ref, upd);
      setContrato((prev) => ({ ...prev, ...upd, updatedAt: new Date() }));
    }
    await logAudit(accion, {
      buildingId: Number(buildingId),
      entityId: String(apartmentId),
      entityType: "alquiler",
      newValue: { ...cambios, contrato: cambios.contrato ? cambios.contrato.nombre : undefined },
    });
  };

  const ejecutar = async (fn, okMsg) => {
    if (guardando) return;
    if (await isDemoUser()) {
      showToast(DEMO_BLOCK_MESSAGE, "warning");
      return;
    }
    setGuardando(true);
    try {
      await fn();
      showToast(okMsg, "success");
      setModal(null);
    } catch (err) {
      console.error("[ContratoAlquiler] no se pudo guardar:", err);
      showToast("No se pudo guardar el contrato.", "error");
    } finally {
      setGuardando(false);
    }
  };

  const guardarNuevo = async () => {
    const monto = Number(alta.monto);
    const tramos = [{ desde: alta.desde, hasta: null, monto, nota: "" }];
    const errs = validarTramos(tramos);
    if (!fondos.some((f) => f.id === form.targetFundId)) errs.push("Elegí un fondo activo.");
    const errArchivo = validarArchivo(archivo);
    if (errArchivo) errs.push(errArchivo);
    setErrores(errs);
    if (errs.length) return;
    await ejecutar(async () => {
      const contratoArchivo = archivo ? await subirArchivo(archivo) : null;
      await escribir({
        targetFundId: form.targetFundId,
        mesCobro: form.mesCobro,
        tramos,
        active: true,
        nota: form.nota.trim(),
        ...(contratoArchivo ? { contrato: contratoArchivo } : {}),
      }, "create_rent_contract");
    }, "Contrato cargado.");
  };

  const guardarAjuste = async () => {
    const r = ajustarMonto(contrato, { desde: ajuste.desde, monto: Number(ajuste.monto), nota: ajuste.nota });
    setErrores(r.errores);
    if (r.errores.length) return;
    await ejecutar(() => escribir({ tramos: r.tramos }, "adjust_rent_contract"),
      `Monto ajustado: ${formatCurrencyUY(Number(ajuste.monto))} desde ${nombrePeriodo(ajuste.desde)}.`);
  };

  const guardarEdicion = async () => {
    const tramos = tramosParaGuardar(form.tramos);
    const errs = validarTramos(tramos);
    if (!tramos.length) errs.unshift("El contrato necesita al menos un tramo con el monto del alquiler.");
    if (!fondos.some((f) => f.id === form.targetFundId)) errs.push("Elegí un fondo activo.");
    const errArchivo = validarArchivo(archivo);
    if (errArchivo) errs.push(errArchivo);
    setErrores(errs);
    if (errs.length) return;
    if (hayAlquileresCobrados && form.mesCobro !== contrato.mesCobro) {
      const ok = await confirmDialog(
        "Ya se cobraron alquileres con el mes de cobro anterior. Cambiarlo puede saltear o repetir un mes (el cierre avisa y no cobra dos veces el mismo período). ¿Cambiar igual?",
        { title: "Cambiar el mes de cobro", confirmLabel: "Cambiar igual", danger: true }
      );
      if (!ok) return;
    }
    await ejecutar(async () => {
      const contratoArchivo = archivo ? await subirArchivo(archivo) : null;
      await escribir({
        targetFundId: form.targetFundId,
        mesCobro: form.mesCobro,
        tramos,
        active: form.active,
        nota: form.nota.trim(),
        ...(contratoArchivo ? { contrato: contratoArchivo } : {}),
      }, "update_rent_contract");
    }, "Contrato actualizado.");
  };

  /** Subir o reemplazar el contrato firmado sin abrir la edición. */
  const subirDesdeTarjeta = async (file) => {
    const err = validarArchivo(file);
    if (err) return showToast(err, "error");
    if (await isDemoUser()) return showToast(DEMO_BLOCK_MESSAGE, "warning");
    setSubiendo(true);
    try {
      const contratoArchivo = await subirArchivo(file);
      await escribir({ contrato: contratoArchivo }, "upload_rent_contract");
      showToast("Contrato subido.", "success");
    } catch (e) {
      console.error("[ContratoAlquiler] no se pudo subir el contrato:", e);
      showToast("No se pudo subir el contrato.", "error");
    } finally {
      setSubiendo(false);
    }
  };

  const cambiarTramo = (i, campo, valor) =>
    setForm((prev) => ({ ...prev, tramos: prev.tramos.map((t, j) => (j === i ? { ...t, [campo]: valor } : t)) }));
  const agregarTramo = () => setForm((prev) => ({ ...prev, tramos: [...prev.tramos, { desde: proximoMesKey(), hasta: "", monto: "", nota: "" }] }));
  const quitarTramo = (i) => setForm((prev) => ({ ...prev, tramos: prev.tramos.filter((_, j) => j !== i) }));

  // ── Campos compartidos por "Cargar" y "Editar" ──
  const camposGenerales = (
    <>
      <FormField label="Fondo destino" htmlFor="alquiler-fondo" hint="A qué fondo entra lo que paga el inquilino.">
        <select
          id="alquiler-fondo"
          className="habitia-input"
          style={styles.input}
          value={form.targetFundId}
          onChange={(e) => setForm((prev) => ({ ...prev, targetFundId: e.target.value }))}
        >
          {fondos.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </FormField>
      <fieldset style={styles.fieldset}>
        <legend style={styles.legend}>Qué mes cobra cada cierre</legend>
        <label style={styles.radio}>
          <input
            type="radio"
            name="alquiler-mes-cobro"
            value={MES_COBRO_ADELANTADO}
            checked={form.mesCobro === MES_COBRO_ADELANTADO}
            onChange={() => setForm((prev) => ({ ...prev, mesCobro: MES_COBRO_ADELANTADO }))}
          />
          <span>
            Por adelantado
            <span style={styles.hint}>El cierre de octubre cobra el alquiler de noviembre.</span>
          </span>
        </label>
        <label style={styles.radio}>
          <input
            type="radio"
            name="alquiler-mes-cobro"
            value={MES_COBRO_VENCIDO}
            checked={form.mesCobro === MES_COBRO_VENCIDO}
            onChange={() => setForm((prev) => ({ ...prev, mesCobro: MES_COBRO_VENCIDO }))}
          />
          <span>
            Mes vencido
            <span style={styles.hint}>El cierre de octubre cobra el alquiler de octubre, como los gastos comunes.</span>
          </span>
        </label>
      </fieldset>
      <FormField label="Contrato firmado (opcional)" htmlFor="alquiler-archivo" fullWidth
        hint={contrato?.contrato && !archivo ? `Actual: ${contrato.contrato.nombre}. Elegí otro archivo para reemplazarlo.` : `PDF, imagen o Word, hasta ${MAX_MB} MB.`}>
        <input
          id="alquiler-archivo"
          type="file"
          accept={TIPOS_ARCHIVO}
          className="habitia-input"
          style={styles.inputArchivo}
          onChange={(e) => setArchivo(e.target.files?.[0] || null)}
        />
      </FormField>
      <FormField label="Nota del contrato" htmlFor="alquiler-nota" fullWidth>
        <input
          id="alquiler-nota"
          className="habitia-input"
          style={styles.input}
          value={form.nota}
          maxLength={300}
          placeholder="Ej.: firmado el 01/11/2026 por 2 años, ajuste anual por IPC"
          onChange={(e) => setForm((prev) => ({ ...prev, nota: e.target.value }))}
        />
      </FormField>
    </>
  );

  const bloqueErrores = errores.length > 0 && (
    <div role="alert" style={{ ...styles.errores, gridColumn: "1 / -1" }}>
      {errores.map((e) => <div key={e}>{e}</div>)}
    </div>
  );

  const pie = (onGuardar, etiqueta) => (
    <>
      <button type="button" style={styles.btnSecondary} className="habitia-btn-secondary" onClick={cerrar} disabled={guardando}>
        Cancelar
      </button>
      <button type="button" style={{ ...styles.btnPrimary, opacity: guardando ? 0.7 : 1 }} className="habitia-btn-primary" onClick={onGuardar} disabled={guardando}>
        {guardando ? "Guardando..." : etiqueta}
      </button>
    </>
  );

  return (
    <div style={{ ...styles.card, gridColumn: "1 / -1" }}>
      <div style={styles.header}>
        <div style={styles.iconBadge}>
          <FaFileContract size={16} color={colors.primary} />
        </div>
        <h3 style={styles.title}>Contrato de alquiler</h3>
        {contrato && (
          <StatusBadge status={contrato.active !== false ? "al_dia" : "cerrada"} label={contrato.active !== false ? "Activo" : "Inactivo"} size="sm" />
        )}
      </div>

      {cargando ? (
        <p style={styles.text}>Cargando contrato…</p>
      ) : errorCarga ? (
        <p style={styles.text}>No se pudo leer el contrato. Probá de nuevo más tarde.</p>
      ) : !contrato ? (
        <>
          <p style={styles.text}>
            Esta unidad es del edificio y se alquila. Todavía no tiene contrato: sin contrato el cierre no le cobra alquiler al inquilino.
          </p>
          <button type="button" style={styles.btnPrimary} className="habitia-btn-primary" onClick={abrirNuevo}>
            <FaPlus size={12} /> Cargar contrato
          </button>
        </>
      ) : (
        <>
          <div style={styles.resumen}>
            <div>
              <span style={styles.etiqueta}>Próximo cobro</span>
              <span style={styles.montoGrande}>
                {proximoCobro?.tramo ? formatCurrencyUY(proximoCobro.tramo.monto) : "Sin monto"}
              </span>
              <span style={styles.hint}>
                {proximoCobro?.tramo
                  ? `Alquiler de ${nombrePeriodo(proximoCobro.periodo)}, en el cierre de ${nombrePeriodo(periodoActual())}`
                  : `Falta el monto de ${nombrePeriodo(proximoCobro?.periodo)}`}
              </span>
            </div>
            <div style={styles.datos}>
              <p style={styles.text}><span style={styles.dato}>Fondo:</span> {nombreFondo(contrato.targetFundId)}</p>
              <p style={styles.text}>
                <span style={styles.dato}>Cobro:</span> {contrato.mesCobro === MES_COBRO_VENCIDO ? "mes vencido" : "por adelantado"}
              </p>
              <p style={styles.text}>
                <span style={styles.dato}>Contrato:</span>{" "}
                {contrato.contrato?.url ? (
                  <a href={contrato.contrato.url} target="_blank" rel="noopener noreferrer" style={styles.link}>
                    {contrato.contrato.nombre || "Ver contrato"} <FaExternalLinkAlt size={10} />
                  </a>
                ) : "sin subir"}
              </p>
              {contrato.nota && <p style={styles.text}><span style={styles.dato}>Nota:</span> {contrato.nota}</p>}
            </div>
          </div>

          <div style={styles.acciones}>
            <button type="button" style={styles.btnPrimary} className="habitia-btn-primary" onClick={abrirAjuste}>
              <FaArrowUp size={12} /> Ajustar monto
            </button>
            <button type="button" style={styles.btnSecondary} className="habitia-btn-secondary" onClick={abrirEditar}>
              <FaPen size={12} /> Editar contrato
            </button>
            <button
              type="button"
              style={styles.btnSecondary}
              className="habitia-btn-secondary"
              onClick={() => inputArchivo.current?.click()}
              disabled={subiendo}
            >
              <FaFileUpload size={12} /> {subiendo ? "Subiendo…" : contrato.contrato?.url ? "Reemplazar contrato" : "Subir contrato"}
            </button>
            <input
              ref={inputArchivo}
              type="file"
              accept={TIPOS_ARCHIVO}
              style={{ display: "none" }}
              aria-label="Archivo del contrato"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) subirDesdeTarjeta(f);
              }}
            />
          </div>

          <p style={styles.seccion}>Historial de montos</p>
          <TablaTramos tramos={contrato.tramos || []} />
        </>
      )}

      {alertas.length > 0 && (
        <div role="status" style={styles.alerta}>
          <FaExclamationTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>{alertas.map((a) => <div key={a}>{a}</div>)}</div>
        </div>
      )}

      {/* ── Cargar contrato ── */}
      <AdminModal open={modal === "nuevo"} onClose={cerrar} title="Cargar contrato de alquiler" size="medium" icon={<FaFileContract />}
        footer={pie(guardarNuevo, "Guardar contrato")}>
        <div style={styles.modalGrid}>
          <FormField label="Alquiler mensual ($)" htmlFor="alquiler-monto" required hint="En pesos, sin centésimos.">
            <input
              id="alquiler-monto"
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              className="habitia-input"
              style={styles.input}
              value={alta.monto}
              placeholder="22000"
              onChange={(e) => setAlta((prev) => ({ ...prev, monto: e.target.value }))}
            />
          </FormField>
          <FormField label="Se cobra desde" htmlFor="alquiler-desde" required hint="Primer mes de alquiler que cobra la plataforma.">
            <SelectorMes id="alquiler-desde" value={alta.desde} onChange={(v) => setAlta((prev) => ({ ...prev, desde: v }))} />
          </FormField>
          {camposGenerales}
          {bloqueErrores}
        </div>
      </AdminModal>

      {/* ── Ajustar monto (sin dar de baja) ── */}
      <AdminModal open={modal === "ajuste"} onClose={cerrar} title="Ajustar el monto del alquiler" size="medium" icon={<FaArrowUp />}
        footer={pie(guardarAjuste, "Guardar ajuste")}>
        <div style={styles.modalGrid}>
          <p style={{ ...styles.text, gridColumn: "1 / -1" }}>
            {proximoCobro?.tramo ? `Hoy: ${formatCurrencyUY(proximoCobro.tramo.monto)} por mes. ` : ""}
            El monto nuevo rige desde el mes que elijas; los meses anteriores quedan con el monto de antes. El contrato sigue activo.
          </p>
          <FormField label="Nuevo alquiler mensual ($)" htmlFor="ajuste-monto" required>
            <input
              id="ajuste-monto"
              type="number"
              min="1"
              step="1"
              inputMode="numeric"
              className="habitia-input"
              style={styles.input}
              value={ajuste.monto}
              onChange={(e) => setAjuste((prev) => ({ ...prev, monto: e.target.value }))}
            />
          </FormField>
          <FormField label="Desde" htmlFor="ajuste-desde" required>
            <SelectorMes id="ajuste-desde" value={ajuste.desde} onChange={(v) => setAjuste((prev) => ({ ...prev, desde: v }))} />
          </FormField>
          <FormField label="Motivo (opcional)" htmlFor="ajuste-nota" fullWidth>
            <input
              id="ajuste-nota"
              className="habitia-input"
              style={styles.input}
              value={ajuste.nota}
              maxLength={120}
              placeholder="Ej.: ajuste anual por IPC"
              onChange={(e) => setAjuste((prev) => ({ ...prev, nota: e.target.value }))}
            />
          </FormField>
          {bloqueErrores}
        </div>
      </AdminModal>

      {/* ── Editar contrato (y corregir tramos) ── */}
      <AdminModal open={modal === "editar"} onClose={cerrar} title="Editar contrato de alquiler" size="wide" icon={<FaPen />}
        footer={pie(guardarEdicion, "Guardar cambios")}>
        <div style={styles.modalGrid}>
          {camposGenerales}
          <FormField label="Estado" htmlFor="alquiler-activo" hint="Un contrato inactivo no se cobra en el cierre (por ejemplo, si la unidad quedó sin inquilino).">
            <label style={styles.check}>
              <input
                id="alquiler-activo"
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
              />
              Contrato activo
            </label>
          </FormField>
          <div style={{ gridColumn: "1 / -1" }}>
            <p style={styles.seccion}>Montos por período</p>
            <p style={styles.hintBloque}>
              Para un aumento usá "Ajustar monto". Acá se corrigen errores de carga.
            </p>
            {form.tramos.map((t, i) => (
              <div key={i} style={styles.tramoFila}>
                <FormField label="Desde" htmlFor={`tramo-${i}-desde`} required>
                  <SelectorMes id={`tramo-${i}-desde`} value={t.desde} onChange={(v) => cambiarTramo(i, "desde", v)} />
                </FormField>
                <FormField label="Hasta" htmlFor={`tramo-${i}-hasta`}>
                  <SelectorMes id={`tramo-${i}-hasta`} value={t.hasta} sinFin onChange={(v) => cambiarTramo(i, "hasta", v)} />
                </FormField>
                <FormField label="Monto ($)" htmlFor={`tramo-${i}-monto`} required>
                  <input
                    id={`tramo-${i}-monto`}
                    type="number"
                    min="1"
                    step="1"
                    inputMode="numeric"
                    className="habitia-input"
                    style={styles.input}
                    value={t.monto}
                    onChange={(e) => cambiarTramo(i, "monto", e.target.value)}
                  />
                </FormField>
                <button
                  type="button"
                  style={styles.quitar}
                  className="habitia-btn-ghost"
                  onClick={() => quitarTramo(i)}
                  aria-label={`Quitar tramo ${i + 1}`}
                  title="Quitar tramo"
                >
                  <FaTrash size={12} />
                </button>
              </div>
            ))}
            <button type="button" style={styles.btnSecondary} className="habitia-btn-secondary" onClick={agregarTramo}>
              <FaPlus size={12} /> Agregar período
            </button>
          </div>
          {bloqueErrores}
        </div>
      </AdminModal>
    </div>
  );
};

/** Tramos del contrato (solo lectura), del más nuevo al más viejo. */
const COLUMNAS_TRAMOS = [
  { key: "desde", label: "Desde", render: (v) => nombrePeriodo(v) },
  { key: "hasta", label: "Hasta", render: (v) => (v ? nombrePeriodo(v) : "Sin fin") },
  { key: "monto", label: "Monto mensual", align: "right", render: (v) => formatCurrencyUY(v) },
  { key: "nota", label: "Motivo", render: (v) => v || "—" },
];

function TablaTramos({ tramos }) {
  const filas = [...tramos]
    .sort((a, b) => String(b.desde).localeCompare(String(a.desde)))
    .map((t) => ({ ...t, id: t.desde }));
  return <DataTable columns={COLUMNAS_TRAMOS} data={filas} dense emptyTitle="El contrato no tiene montos cargados" />;
}

const styles = {
  card: { ...cardBase, display: "flex", flexDirection: "column", gap: 10 },
  header: { display: "flex", alignItems: "center", gap: 10, marginBottom: 4 },
  iconBadge: {
    width: 36, height: 36, borderRadius: radius.sm, background: colors.primaryLighter,
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  title: { margin: 0, fontSize: typography.sizes.lg, fontWeight: typography.weights.normal, color: colors.black },
  text: { fontSize: typography.sizes.base, margin: 0, color: colors.gray600 },
  dato: { color: colors.black },
  datos: { display: "flex", flexDirection: "column", gap: 4 },
  resumen: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: spacing[3],
    padding: spacing[3], background: colors.gray50, borderRadius: radius.sm,
  },
  etiqueta: { display: "block", fontSize: typography.sizes.sm, color: colors.gray600 },
  montoGrande: { display: "block", fontSize: typography.sizes["2xl"], color: colors.black, margin: "2px 0" },
  link: { color: colors.primary, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 },
  acciones: { display: "flex", gap: spacing[2], flexWrap: "wrap" },
  alerta: {
    display: "flex", gap: spacing[2], alignItems: "flex-start", padding: `${spacing[2]}px ${spacing[3]}px`,
    background: colors.warningLight, color: colors.warningDark, borderRadius: radius.sm, fontSize: typography.sizes.sm,
  },
  errores: {
    padding: `${spacing[2]}px ${spacing[3]}px`, background: colors.errorLight, color: colors.errorDark,
    borderRadius: radius.sm, fontSize: typography.sizes.sm, display: "flex", flexDirection: "column", gap: 4,
  },
  modalGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: spacing[3] },
  fieldset: { gridColumn: "1 / -1", border: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: spacing[2] },
  legend: { fontSize: typography.sizes.base, color: colors.gray900, marginBottom: spacing[1], padding: 0 },
  radio: { display: "flex", gap: spacing[2], alignItems: "flex-start", fontSize: typography.sizes.base, color: colors.gray900, cursor: "pointer" },
  check: { display: "flex", gap: spacing[2], alignItems: "center", fontSize: typography.sizes.base, color: colors.gray900, minHeight: 36 },
  checkChico: { display: "flex", gap: 6, alignItems: "center", fontSize: typography.sizes.sm, color: colors.gray600, whiteSpace: "nowrap" },
  hint: { display: "block", fontSize: typography.sizes.sm, color: colors.gray600 },
  hintBloque: { margin: `0 0 ${spacing[2]}px`, fontSize: typography.sizes.sm, color: colors.gray600 },
  seccion: { margin: `${spacing[1]}px 0 ${spacing[1]}px`, fontSize: typography.sizes.base, color: colors.black },
  selectorMes: { display: "flex", gap: spacing[2], alignItems: "center", flexWrap: "wrap" },
  tramoFila: {
    display: "grid", gridTemplateColumns: "minmax(220px, 1.2fr) minmax(260px, 1.4fr) minmax(120px, 0.8fr) 36px", gap: spacing[2],
    alignItems: "end", marginBottom: spacing[2], paddingBottom: spacing[2], borderBottom: `1px solid ${colors.gray100}`,
  },
  quitar: { ...btnGhost, color: colors.gray600, height: 36 },
  input: { ...inputBase, height: 36, padding: "6px 10px" },
  inputArchivo: { ...inputBase, padding: "6px 10px" },
  btnPrimary: { ...btnPrimary, alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 6 },
  btnSecondary: { ...btnSecondary, display: "inline-flex", alignItems: "center", gap: 6 },
};

export default ContratoAlquilerCard;
